'use client';

import { c as _c } from "react/compiler-runtime";
import Bugsnag from '@bugsnag/js';
import { ExclamationTriangleIcon } from '@radix-ui/react-icons';
import { XIcon } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { type ReactNode, useState } from 'react';
import { ErrorBoundary as ErrorBoundaryComp } from 'react-error-boundary';
import { isDevelopment } from '@/lib/general-helpers';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { Button } from '../ui/button';
function ErrorBoundaryFallback(t0) {
  const $ = _c(42);
  const {
    error,
    resetErrorBoundary,
    customErrorTitle,
    hideReset,
    showErrorMessage
  } = t0;
  const [hidden, setHidden] = useState(false);
  const t = useTranslations("ErrorBoundary");
  let T0;
  let T1;
  let handleTryAgain;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[0] !== customErrorTitle || $[1] !== error || $[2] !== hidden || $[3] !== resetErrorBoundary || $[4] !== showErrorMessage || $[5] !== t) {
    t7 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const err = error instanceof Error ? error : new Error(String(error));
      if (hidden) {
        t7 = null;
        break bb0;
      }
      let t8;
      if ($[16] !== resetErrorBoundary) {
        t8 = () => {
          resetErrorBoundary();
        };
        $[16] = resetErrorBoundary;
        $[17] = t8;
      } else {
        t8 = $[17];
      }
      handleTryAgain = t8;
      let t9;
      if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
        t9 = isDevelopment();
        $[18] = t9;
      } else {
        t9 = $[18];
      }
      const isDev = t9;
      T1 = Alert;
      t3 = "destructive";
      t4 = "relative";
      if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
        t5 = <ExclamationTriangleIcon className="size-4" />;
        $[19] = t5;
      } else {
        t5 = $[19];
      }
      let t10;
      if ($[20] !== customErrorTitle || $[21] !== t) {
        t10 = customErrorTitle ?? t("invalidContent");
        $[20] = customErrorTitle;
        $[21] = t;
        $[22] = t10;
      } else {
        t10 = $[22];
      }
      if ($[23] !== t10) {
        t6 = <AlertTitle>{t10}</AlertTitle>;
        $[23] = t10;
        $[24] = t6;
      } else {
        t6 = $[24];
      }
      T0 = AlertDescription;
      t1 = (showErrorMessage || isDev) && <p className="mt-1 text-base text-black">{err.message}</p>;
      t2 = isDev && <div className="mt-2 w-full overflow-x-auto bg-gray-100 p-3 text-sm"><pre>{err.stack?.split("\n").slice(0, 5).join("\n")}</pre></div>;
    }
    $[0] = customErrorTitle;
    $[1] = error;
    $[2] = hidden;
    $[3] = resetErrorBoundary;
    $[4] = showErrorMessage;
    $[5] = t;
    $[6] = T0;
    $[7] = T1;
    $[8] = handleTryAgain;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    T0 = $[6];
    T1 = $[7];
    handleTryAgain = $[8];
    t1 = $[9];
    t2 = $[10];
    t3 = $[11];
    t4 = $[12];
    t5 = $[13];
    t6 = $[14];
    t7 = $[15];
  }
  if (t7 !== Symbol.for("react.early_return_sentinel")) {
    return t7;
  }
  let t8;
  if ($[25] !== handleTryAgain || $[26] !== hideReset || $[27] !== t) {
    t8 = !hideReset && <Button type="button" size="medium" variant="primary" onClick={handleTryAgain} className="mt-2">{t("tryAgain")}</Button>;
    $[25] = handleTryAgain;
    $[26] = hideReset;
    $[27] = t;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[29] !== T0 || $[30] !== t1 || $[31] !== t2 || $[32] !== t8) {
    t9 = <T0>{t1}{t2}{t8}</T0>;
    $[29] = T0;
    $[30] = t1;
    $[31] = t2;
    $[32] = t8;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  let t10;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <span className="absolute right-2 top-2 block"><XIcon className="size-4 cursor-pointer" onClick={() => setHidden(true)} /></span>;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  let t11;
  if ($[35] !== T1 || $[36] !== t3 || $[37] !== t4 || $[38] !== t5 || $[39] !== t6 || $[40] !== t9) {
    t11 = <T1 variant={t3} className={t4}>{t5}{t6}{t9}{t10}</T1>;
    $[35] = T1;
    $[36] = t3;
    $[37] = t4;
    $[38] = t5;
    $[39] = t6;
    $[40] = t9;
    $[41] = t11;
  } else {
    t11 = $[41];
  }
  return t11;
}
export function ErrorBoundary(t0) {
  const $ = _c(12);
  const {
    children,
    hideReset,
    hideFallback,
    customErrorTitle,
    showErrorMessage,
    onReset,
    onError
  } = t0;
  let t1;
  if ($[0] !== onError) {
    t1 = (error, info) => {
      const err = error instanceof Error ? error : new Error(String(error));
      if (onError) {
        onError(err, info);
      }
      Bugsnag.notify(err, event => {
        if (info.componentStack) {
          event.addMetadata("component", {
            stack: info.componentStack
          });
        }
        return true;
      });
    };
    $[0] = onError;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleError = t1;
  let t2;
  if ($[2] !== customErrorTitle || $[3] !== hideFallback || $[4] !== hideReset || $[5] !== showErrorMessage) {
    t2 = props => hideFallback ? null : <ErrorBoundaryFallback {...props} hideReset={hideReset} customErrorTitle={customErrorTitle} showErrorMessage={showErrorMessage} />;
    $[2] = customErrorTitle;
    $[3] = hideFallback;
    $[4] = hideReset;
    $[5] = showErrorMessage;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== children || $[8] !== handleError || $[9] !== onReset || $[10] !== t2) {
    t3 = <ErrorBoundaryComp fallbackRender={t2} onError={handleError} onReset={onReset}>{children}</ErrorBoundaryComp>;
    $[7] = children;
    $[8] = handleError;
    $[9] = onReset;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
}